import React, {useEffect, useRef} from 'react';

const ACM_LAT_LNG = {lat: 34.398453, lng: 133.083841};
const MAPS_API_KEY = 'AIzaSyATLBC1jcjQYC3m0kUXWsS3CE7-EXFZMRw';
const CALLBACK_NAME = '_initGoogleMapForTopPage';

declare global {
  interface Window {
    google: any;
    [CALLBACK_NAME]?: () => void;
  }
}

// Singleton to prevent double-loading in React StrictMode
let mapsApiPromise: Promise<void> | null = null;

export function loadMapsApi(): Promise<void> {
  if (typeof window.google?.maps !== 'undefined') return Promise.resolve();
  if (mapsApiPromise !== null) return mapsApiPromise;

  mapsApiPromise = new Promise<void>((resolve, reject) => {
    window[CALLBACK_NAME] = resolve;
    const nonce =
      document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')
        ?.content ?? '';
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&loading=async&callback=${CALLBACK_NAME}&libraries=marker`;
    script.async = true;
    script.onerror = () => {
      mapsApiPromise = null;
      reject(new Error('Failed to load Google Maps API'));
    };
    if (nonce) script.setAttribute('nonce', nonce);
    document.head.appendChild(script);
  });

  return mapsApiPromise;
}

function getMarkerUrl(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="map-marker-url"]')
      ?.content ?? '/assets/map-marker@3x.png'
  );
}

async function initMap(el: HTMLElement): Promise<void> {
  const g = (window as any).google;
  const {Map} = await g.maps.importLibrary('maps');
  const {AdvancedMarkerElement} = await g.maps.importLibrary('marker');

  const map = new Map(el, {
    zoom: 16,
    center: ACM_LAT_LNG,
    mapTypeControl: false,
    panControl: false,
    mapId: 'cf2b62356780d34a4438e3f2',
  });

  const img = document.createElement('img');
  img.src = getMarkerUrl();
  img.width = 80;
  img.height = 98;

  new AdvancedMarkerElement({map, position: ACM_LAT_LNG, content: img});
}

export const GoogleMapView: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mapRef.current) return;
      try {
        await loadMapsApi();
        if (!cancelled && mapRef.current) {
          await initMap(mapRef.current);
        }
      } catch (e) {
        console.error('Google Maps failed to initialize:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <div ref={mapRef} className="size-full min-h-64" />;
};
